import type { Attachment } from './attachment';

export type PendingUploadStatus = 'uploading' | 'staged' | 'failed';

export interface PendingAttachment {
  clientId: string;
  file: File | null;
  attachment: Attachment | null;
  uploadStatus: PendingUploadStatus;
  uploadError?: string;
  note: string;
  include_on_send: boolean;
}

export interface EditableAttachment {
  attachment: Attachment;
  note: string;
  include_on_send: boolean;
  markedForDelete: boolean;
}

export const attachmentToEditable = (attachment: Attachment): EditableAttachment => ({
  attachment,
  note: attachment.note ?? '',
  include_on_send: attachment.include_on_send,
  markedForDelete: false,
});

export const isEditableAttachmentDirty = (row: EditableAttachment): boolean => {
  if (row.markedForDelete) return true;
  const noteChanged = (row.note || '') !== (row.attachment.note || '');
  const sendChanged = row.include_on_send !== row.attachment.include_on_send;
  return noteChanged || sendChanged;
};

export const createPendingAttachment = (file: File): PendingAttachment => ({
  clientId:
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  file,
  attachment: null,
  uploadStatus: 'uploading',
  note: '',
  include_on_send: false,
});

export const stagedAttachmentToPending = (attachment: Attachment): PendingAttachment => ({
  clientId: String(attachment.id),
  file: null,
  attachment,
  uploadStatus: 'staged',
  note: attachment.note ?? '',
  include_on_send: attachment.include_on_send,
});

export const getStagedAttachmentIds = (pending: PendingAttachment[]): number[] =>
  pending
    .filter((row) => row.uploadStatus === 'staged' && row.attachment?.id)
    .map((row) => row.attachment!.id);

export const hasPendingUploadInProgress = (pending: PendingAttachment[]): boolean =>
  pending.some((row) => row.uploadStatus === 'uploading');

export const hasPendingUploadFailed = (pending: PendingAttachment[]): boolean =>
  pending.some((row) => row.uploadStatus === 'failed');

export const getPendingDisplayName = (row: PendingAttachment): string =>
  row.attachment?.filename ?? row.file?.name ?? 'Unknown file';

export const getPendingDisplaySize = (row: PendingAttachment): number =>
  row.attachment?.byte_size ?? row.file?.size ?? 0;

export const isStagedNoteDirty = (row: PendingAttachment): boolean => {
  if (row.uploadStatus !== 'staged' || !row.attachment) return false;
  return (row.note || '') !== (row.attachment.note || '');
};

/** Map staged attachment ids → include_on_send for invoice create/update link. */
export const buildIncludeOnSendById = (
  pending: PendingAttachment[]
): Record<string, boolean> | undefined => {
  const staged = pending.filter((row) => row.uploadStatus === 'staged' && row.attachment?.id);
  if (staged.length === 0) return undefined;

  const map: Record<string, boolean> = {};
  for (const row of staged) {
    map[String(row.attachment!.id)] = row.include_on_send;
  }
  return map;
};
